import React, { useEffect, useRef, useState, useCallback } from "react";
import "./FloatingPdfViewer.css";

/**
 * Floating, draggable, resizable PDF-Viewer für die Rename-Engine.
 *
 * Kein Popup/Modal — bleibt frei auf der Seite. Drag am Header, Resize am
 * unteren rechten Eck. Wenn sich `source` ändert (anderer Klick auf eine PDF),
 * lädt der Viewer die neue Datei in der gleichen Position/Größe weiter.
 *
 * Props:
 *   - source: { url?: string, file?: File, name?: string } | null
 *   - onClose: () => void
 */
export default function FloatingPdfViewer({ source, onClose }) {
  const [pos, setPos] = useState(() => ({
    x: Math.max(20, (typeof window !== "undefined" ? window.innerWidth - 540 : 100)),
    y: 110,
  }));
  const [size, setSize] = useState({ w: 500, h: 640 });
  const [pageIdx, setPageIdx] = useState(0);
  const [pageCount, setPageCount] = useState(0);
  const [zoom, setZoom] = useState(1);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const pdfDocRef = useRef(null);
  const renderTaskRef = useRef(null);

  // --- pdfjs-Loader (lazy, mit Fallback wie in RenamePage) ---
  const loadPdfjs = useCallback(async () => {
    let lib, usingLegacy = false;
    try {
      lib = await import(/* webpackChunkName: "pdfjs-legacy" */ "pdfjs-dist/legacy/build/pdf.mjs");
      usingLegacy = true;
    } catch (_) {
      try {
        lib = await import(/* webpackChunkName: "pdfjs" */ "pdfjs-dist/build/pdf.mjs");
      } catch (_) {
        lib = await import("pdfjs-dist");
      }
    }
    try {
      const ver = lib?.version || "4.10.38";
      lib.GlobalWorkerOptions.workerSrc = usingLegacy
        ? `https://unpkg.com/pdfjs-dist@${ver}/legacy/build/pdf.worker.min.mjs`
        : `https://unpkg.com/pdfjs-dist@${ver}/build/pdf.worker.min.mjs`;
    } catch (_) {}
    return lib;
  }, []);

  // --- PDF laden bei source-Wechsel ---
  useEffect(() => {
    let aborted = false;
    if (!source) {
      pdfDocRef.current = null;
      setPageCount(0);
      setPageIdx(0);
      return () => { aborted = true; };
    }
    setErr("");
    setLoading(true);
    (async () => {
      try {
        const lib = await loadPdfjs();
        let data;
        if (source.file) {
          data = await source.file.arrayBuffer();
        } else if (source.url) {
          const res = await fetch(source.url, { credentials: "include" });
          if (!res.ok) {
            let detail = "";
            try {
              const j = await res.json();
              detail = j?.detail || "";
            } catch (_) {}
            if (res.status === 404) {
              throw new Error(detail || "Datei in Dropbox nicht gefunden. Liste neu laden (Breadcrumb anklicken).");
            }
            throw new Error(detail || `HTTP ${res.status}`);
          }
          data = await res.arrayBuffer();
        } else {
          throw new Error("Keine Quelle angegeben");
        }
        if (aborted) return;
        const pdf = await lib.getDocument({ data }).promise;
        if (aborted) return;
        pdfDocRef.current = pdf;
        setPageCount(pdf.numPages);
        setPageIdx(0);
      } catch (e) {
        if (!aborted) setErr(e?.message || String(e));
      } finally {
        if (!aborted) setLoading(false);
      }
    })();
    return () => { aborted = true; };
  }, [source, loadPdfjs]);

  // --- Seite rendern ---
  const renderPage = useCallback(async () => {
    const pdf = pdfDocRef.current;
    const canvas = canvasRef.current;
    const cont = containerRef.current;
    if (!pdf || !canvas || !cont) return;
    try {
      if (renderTaskRef.current) {
        try { renderTaskRef.current.cancel(); } catch (_) {}
        renderTaskRef.current = null;
      }
      const page = await pdf.getPage(pageIdx + 1);
      const baseViewport = page.getViewport({ scale: 1 });
      const contentWidth = Math.max(120, cont.clientWidth - 4);
      const fitScale = contentWidth / baseViewport.width;
      const scale = Math.max(0.2, zoom) * fitScale;
      const viewport = page.getViewport({ scale });
      const dpr = window.devicePixelRatio || 1;
      canvas.style.width = `${Math.floor(viewport.width)}px`;
      canvas.style.height = `${Math.floor(viewport.height)}px`;
      canvas.width = Math.floor(viewport.width * dpr);
      canvas.height = Math.floor(viewport.height * dpr);
      const ctx = canvas.getContext("2d");
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, viewport.width, viewport.height);
      const task = page.render({ canvasContext: ctx, viewport });
      renderTaskRef.current = task;
      await task.promise;
      renderTaskRef.current = null;
    } catch (e) {
      if (e?.name !== "RenderingCancelledException") {
        setErr(e?.message || String(e));
      }
    }
  }, [pageIdx, zoom]);

  useEffect(() => { renderPage(); }, [renderPage, size.w, size.h, pageCount]);

  // --- Drag-Logik (Header) ---
  const dragRef = useRef(null);
  const onHeaderPointerDown = (e) => {
    if (e.target.closest(".fpv-no-drag")) return;
    dragRef.current = { dx: e.clientX - pos.x, dy: e.clientY - pos.y };
    e.currentTarget.setPointerCapture(e.pointerId);
  };
  const onHeaderPointerMove = (e) => {
    if (!dragRef.current) return;
    const nx = Math.max(0, Math.min(window.innerWidth - 80, e.clientX - dragRef.current.dx));
    const ny = Math.max(0, Math.min(window.innerHeight - 40, e.clientY - dragRef.current.dy));
    setPos({ x: nx, y: ny });
  };
  const onHeaderPointerUp = (e) => {
    dragRef.current = null;
    try { e.currentTarget.releasePointerCapture(e.pointerId); } catch (_) {}
  };

  // --- Resize-Logik (Eck-Handle) ---
  const resizeRef = useRef(null);
  const onResizePointerDown = (e) => {
    resizeRef.current = { sx: e.clientX, sy: e.clientY, w0: size.w, h0: size.h };
    e.currentTarget.setPointerCapture(e.pointerId);
    e.stopPropagation();
  };
  const onResizePointerMove = (e) => {
    if (!resizeRef.current) return;
    const dx = e.clientX - resizeRef.current.sx;
    const dy = e.clientY - resizeRef.current.sy;
    const nw = Math.max(280, Math.min(window.innerWidth - pos.x - 8, resizeRef.current.w0 + dx));
    const nh = Math.max(220, Math.min(window.innerHeight - pos.y - 8, resizeRef.current.h0 + dy));
    setSize({ w: nw, h: nh });
  };
  const onResizePointerUp = (e) => {
    resizeRef.current = null;
    try { e.currentTarget.releasePointerCapture(e.pointerId); } catch (_) {}
  };

  if (!source) return null;

  return (
    <div
      className="fpv-root"
      style={{ left: pos.x, top: pos.y, width: size.w, height: size.h }}
      role="dialog"
      aria-label="PDF-Vorschau"
    >
      <div
        className="fpv-header"
        onPointerDown={onHeaderPointerDown}
        onPointerMove={onHeaderPointerMove}
        onPointerUp={onHeaderPointerUp}
        onPointerCancel={onHeaderPointerUp}
      >
        <span className="fpv-title" title={source.name || ""}>
          📄 {source.name || "PDF"}
        </span>
        <div className="fpv-controls fpv-no-drag">
          <button
            className="fpv-btn"
            onClick={() => setPageIdx((p) => Math.max(0, p - 1))}
            disabled={pageIdx <= 0}
            title="Vorherige Seite"
          >‹</button>
          <span className="fpv-page">{pageCount ? `${pageIdx + 1} / ${pageCount}` : "–"}</span>
          <button
            className="fpv-btn"
            onClick={() => setPageIdx((p) => Math.min(pageCount - 1, p + 1))}
            disabled={pageIdx >= pageCount - 1}
            title="Nächste Seite"
          >›</button>
          <span className="fpv-sep" />
          <button className="fpv-btn" onClick={() => setZoom((z) => Math.max(0.4, z - 0.2))} title="Zoom −">−</button>
          <button className="fpv-btn" onClick={() => setZoom(1)} title="100 %">{Math.round(zoom * 100)}%</button>
          <button className="fpv-btn" onClick={() => setZoom((z) => Math.min(4, z + 0.2))} title="Zoom +">+</button>
          <span className="fpv-sep" />
          <button className="fpv-btn fpv-close" onClick={onClose} title="Schließen">✕</button>
        </div>
      </div>
      <div className="fpv-body" ref={containerRef}>
        {loading && <div className="fpv-status">lädt…</div>}
        {err && !loading && <div className="fpv-status fpv-error">{err}</div>}
        <canvas ref={canvasRef} className="fpv-canvas" />
      </div>
      <div
        className="fpv-resize"
        onPointerDown={onResizePointerDown}
        onPointerMove={onResizePointerMove}
        onPointerUp={onResizePointerUp}
        onPointerCancel={onResizePointerUp}
        title="Größe ändern"
      />
    </div>
  );
}
